import React, { useRef, useState } from "react";
import { clsx } from "clsx";
import { CachedAvatar } from "@/shared/components/ui/cached-avatar";
import { useAuth } from "@/contexts/AuthContext";
import { FiChevronDown } from "react-icons/fi";
import { SidebarFlyout } from "../SidebarFlyout";
import { AccountMenuContent, useAccountSessions } from "./AccountMenuContent";
import { AllSessionsDialog } from "@/shared/components/dialogs/AllSessionsDialog";
import { useCompanyDrawer } from "@/contexts/CompanyDrawerContext";

// `compact`: usado no rail (64px) da sidebar — mostra só o avatar (sem nome/e-mail/chevron).
// Flyout via SidebarFlyout direction 'up' — MESMA geometria do menu Configurações
// (padrão dos dropdowns da sidebar), substituindo o antigo Radix DropdownMenu que
// abria com offset/z-index divergentes (e ficava sob o scrim no mobile).
export const UserProfileDropdown: React.FC<{ compact?: boolean }> = ({ compact = false }) => {
  const [open, setOpen] = useState(false);
  const [showAllSessions, setShowAllSessions] = useState(false);
  // A gaveta de Empresa é montada UMA vez, na Sidebar. Aqui só disparamos a
  // abertura pelo contexto — antes este componente montava uma SEGUNDA cópia do
  // mesmo `CompanyEditDrawer`, com estado próprio, em paralelo à da Sidebar.
  const { open: openCompanyDrawer } = useCompanyDrawer();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { state } = useAuth();
  const { otherSessions, switchTo, removeSession } = useAccountSessions();

  const user = state.user;

  // Bloco de conta mostra nome completo (mockup .acct: "Henrique Pereira")
  const displayName = user ? [user.firstName, user.lastName].filter(Boolean).join(' ') : "Usuário";
  const displayEmail = user?.email || "";

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        data-testid="account-menu-trigger"
        className={clsx('flex items-center text-sm focus:outline-none transition-all duration-200', compact ? 'rounded-full' : 'w-full space-x-3 rounded-[10px] px-2 py-[7px]')}
        style={compact ? undefined : { color: 'var(--theme-sidebar-text)' }}
        onMouseEnter={!compact ? (e) => { e.currentTarget.style.backgroundColor = 'rgba(148,163,184,0.14)'; } : undefined}
        onMouseLeave={!compact ? (e) => { e.currentTarget.style.backgroundColor = 'transparent'; } : undefined}
        onClick={() => setOpen((v) => !v)}
      >
        <CachedAvatar
          src={user?.avatar}
          alt={displayName}
          size="md"
          isSquircle={true}
        />
        {!compact && (
          <>
            {/* Sem `hidden md:block`: a visibilidade compacta é SÓ da prop compact —
                a classe responsiva escondia nome/e-mail no drawer mobile (<768px). */}
            <div className="text-left flex-1 min-w-0">
              <div
                className="text-sm font-semibold leading-tight truncate"
                style={{ color: 'var(--theme-sidebar-text-hover)' }}
              >
                {displayName}
              </div>
              <div
                className="text-xs opacity-75 truncate"
                style={{ color: 'var(--theme-sidebar-text)' }}
              >
                {displayEmail}
              </div>
            </div>
            <FiChevronDown
              className="h-4 w-4 opacity-75 transition-transform duration-200"
              style={{
                color: 'var(--theme-sidebar-text)',
                transform: open ? 'rotate(180deg)' : 'rotate(0deg)'
              }}
            />
          </>
        )}
      </button>

      <SidebarFlyout
        triggerEl={triggerRef.current}
        direction="up"
        isOpen={open}
        onClose={() => setOpen(false)}
        width={256}
        data-testid="account-flyout-shell"
      >
        <AccountMenuContent
          variant="flyout"
          onClose={() => setOpen(false)}
          onOpenAllSessions={() => setShowAllSessions(true)}
          onOpenCompanyDrawer={openCompanyDrawer}
        />
      </SidebarFlyout>

      {/* All Sessions Dialog */}
      <AllSessionsDialog
        open={showAllSessions}
        onOpenChange={setShowAllSessions}
        sessions={otherSessions}
        onSessionSelect={(session) => { void switchTo(session); }}
        onSessionDelete={removeSession}
      />
    </>
  );
};
